import { createContext, PropsWithChildren } from 'react'

// All api clients use the same, identical configuration.
// Thanks to TypeScript's ability to unify types based on commonalities,
//  we can accept passing the user-management configuration to all api clients.
import { Configuration } from '../../_generated/api/user-management/configuration'

import {
    EulaApi,
    IdentityApi,
    TenantApi,
    TenantUserApi,
} from '../../_generated/api/user-management'
import {
    BoltApi,
    CalibrationRodApi,
    SensorApi,
    DeviceApi,
    CalibrationApi,
} from '../../_generated/api/tagged-hardware'
import {
    BoltConnectionApi,
    ProjectApi,
    ServiceWindowApi,
    TreeNodeApi,
} from '../../_generated/api/structure'
import { SearchApi } from '../../_generated/api/search'
import { FileExportApi } from '../../_generated/api/export'
import { WorkFileImportApi } from '../../_generated/api/workfile-import'

import { useAuthContext } from './AuthContext'
import { useProvideContext } from '../../hooks/context'
import { getEnvString } from '../../tools/env'

interface ApiContextValue {
    deviceApi: DeviceApi
    projectApi: ProjectApi
    boltConnectionApi: BoltConnectionApi
    treeNodeApi: TreeNodeApi
    serviceWindowApi: ServiceWindowApi
    boltApi: BoltApi
    exportApi: FileExportApi
    calibrationRodApi: CalibrationRodApi
    sensorApi: SensorApi
    calibrationApi: CalibrationApi
    userApi: TenantUserApi
    eulaApi: EulaApi
    organizationApi: TenantApi
    identityApi: IdentityApi
    searchApi: SearchApi
    workfileApi: WorkFileImportApi
}

const ApiContext = createContext<ApiContextValue | undefined>(undefined)

export default function ({ children }: PropsWithChildren) {
    const { accessToken } = useAuthContext()

    const configuration = new Configuration({
        basePath: getEnvString('API_BASE_PATH'),
        // Axios options: https://axios-http.com/docs/req_config
        baseOptions: {
            // Disable internal headers by passing an empty object.
            // The generator forcibly adds an illegal user-agent header under the hood causing console errors.
            headers: {},
            // Let Axios know that every HTTP status is valid and that it should never throw an error.
            validateStatus: () => true,
        },
        accessToken: accessToken ?? undefined,
    })

    const value: ApiContextValue = {
        deviceApi: new DeviceApi(configuration),
        projectApi: new ProjectApi(configuration),
        boltConnectionApi: new BoltConnectionApi(configuration),
        treeNodeApi: new TreeNodeApi(configuration),
        serviceWindowApi: new ServiceWindowApi(configuration),
        boltApi: new BoltApi(configuration),
        exportApi: new FileExportApi(configuration),
        calibrationRodApi: new CalibrationRodApi(configuration),
        sensorApi: new SensorApi(configuration),
        calibrationApi: new CalibrationApi(configuration),
        userApi: new TenantUserApi(configuration),
        eulaApi: new EulaApi(configuration),
        organizationApi: new TenantApi(configuration),
        identityApi: new IdentityApi(configuration),
        searchApi: new SearchApi(configuration),
        workfileApi: new WorkFileImportApi(configuration),
    }

    return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>
}

export const useApiContext = () => useProvideContext(ApiContext, 'Api')
